import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { PayableContractError } from "../state/types"
import { PayableContract } from "src/common/models/payableContract"
import { Alert, DatePicker, Form, Input, Row, Select } from "antd"
import { Col6, Col8 } from "src/components/Columns"
import { amountValidator, formatToDisplayDate, getInputRequiredRule } from "src/common/util"
import { SystemDescriptions } from "src/common/descriptions"
import { ServiceCategory } from "src/common/models/serviceCategory"
import { useEffect, useState } from "react"
import { getServiceCategoriesApi } from "src/features/Service/state/action"
import dayjs from 'dayjs'
import { SystemConstants } from "src/common/constants"
import { FormActionButtons } from "src/components/FormActionButtons"
import { CurrencyInput } from "src/components/CurrencyInput"

interface ReduxProps {
    error?: PayableContractError
    serviceCategories: ServiceCategory[]
}

interface Props {
    initialValues?: PayableContract
    onFinish: (payableContract: Partial<PayableContract>) => void
    onCancel: () => void
}

export const PayableContractForm = (props: Props) => {
    const dispatch = useDispatch()

    const [isMonthlyContract, setIsMonthlyContract] = useState<boolean>(false)

    useEffect(() => {
        dispatch(getServiceCategoriesApi())

        form.setFieldsValue({
            startDate: dayjs()
        })
    }, [])

    useEffect(() => {
        if (props.initialValues) {
            form.setFieldsValue({
                description: props.initialValues.description,
                serviceCategoryId: props.initialValues.serviceCategoryId,
                amount: props.initialValues.total,
                isMonthly: props.initialValues.isMonthly,
                startDate: dayjs(props.initialValues.startDate),
                paymentDay: props.initialValues.payDate,
                nextServiceDebit: formatToDisplayDate(props.initialValues.nextServiceDebit),
            })
        }
    }, [props.initialValues])

    const [form] = Form.useForm();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.payableContract.error,
        serviceCategories: state.service.serviceCategories || [],
    }))

    const descriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.CREATE_PAYABLE_CONTRACT
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const onFinish = (formValues) => {
        const isNew = !props.initialValues?.id;

        const startDate = dayjs(formValues.startDate).format('YYYY-MM-DD');

        const payableContract: PayableContract = {
            isMonthly: formValues.isMonthly,
            payDate: formValues.paymentDay,
            startDate,
            total: Number(formValues.amount),
            lastServiceDebit: formValues.lastServiceDebit || startDate,
            nextServiceDebit: isNew
                ? getNextPaymentDate(formValues.startDate, formValues.paymentDay).format('YYYY-MM-DD')
                : getNextPaymentDate(dayjs().format('YYYY-MM-DD'), formValues.paymentDay).format('YYYY-MM-DD'),
            isActive: true,
            characteristics: [],
            serviceCategoryId: formValues.serviceCategoryId,
            description: formValues.description,
        };

        props.onFinish(payableContract)
    }

    const getNextPaymentDate = (startDate: string, paymentDay: number) => {
        const start = dayjs(startDate);
        const nextMonth = start.add(1, "month");
        const lastDayOfNextMonth = nextMonth.endOf("month").date();

        const paymentDate = nextMonth.date(Math.min(paymentDay, lastDayOfNextMonth));

        return paymentDate
    };

    // useEffect(() => {
    //     if (isMonthlyContract) {
    //         const { startDate } = form.getFieldsValue()

    //         const paymentDay = startDate.format('D')
    //         const nextServiceDebit = getNextPaymentDate(startDate, paymentDay)

    //         form.setFieldsValue({
    //             paymentDay,
    //             nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
    //         })
    //     }
    // }, [isMonthlyContract])


    const handleIsMonthlyChange = (isMonthly: string) => {
        const isMonthlyFlag: boolean = Number(isMonthly) === 1

        setIsMonthlyContract(isMonthlyFlag)

        const { startDate } = form.getFieldsValue()

        const paymentDay = startDate.format('D')
        const nextServiceDebit = getNextPaymentDate(startDate, paymentDay)

        form.setFieldsValue({
            paymentDay,
            nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
        })
    }

    const handleStartDateChange = (e) => {
        const paymentDay = e.format('D') || undefined
        const startDate = dayjs(e).format('YYYY-MM-DD')
        const nextServiceDebit = getNextPaymentDate(startDate, paymentDay)

        form.setFieldsValue({
            paymentDay,
            nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
        })
    }

    const handlePaymentDayChange = (e) => {
        const { startDate } = form.getFieldsValue()

        const paymentDay = e.target.value ? e.target.value : undefined
        const nextServiceDebit = getNextPaymentDate(startDate, paymentDay)

        form.setFieldsValue({
            paymentDay,
            nextServiceDebit: nextServiceDebit.format('DD-MM-YYYY'),
        })
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.DESCRIPTION.LABEL}
                        name={"description"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.DESCRIPTION.LABEL)
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.DESCRIPTION.PLACEHOLDER}
                            maxLength={25}
                            showCount
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.SERVICE_CATEGORY.LABEL}
                        name={"serviceCategoryId"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.SERVICE_CATEGORY.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={reduxProps.serviceCategories.map(serviceCategory => ({
                                value: serviceCategory.id,
                                label: serviceCategory.name,
                            }))}
                            placeholder={descriptions.FORM.SERVICE_CATEGORY.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.AMOUNT.LABEL}
                        name={"amount"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.AMOUNT.LABEL),
                            { validator: (_, value) => amountValidator(value) },
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.AMOUNT.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.IS_MONTHLY.LABEL}
                        name={"isMonthly"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.IS_MONTHLY.LABEL)
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    value: 1,
                                    label: commonDescriptions.YES,
                                },
                                {
                                    value: 0,
                                    label: commonDescriptions.NO,
                                },
                            ]}
                            placeholder={descriptions.FORM.IS_MONTHLY.PLACEHOLDER}
                            onChange={handleIsMonthlyChange}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.START_DATE.LABEL}
                        name={"startDate"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.START_DATE.LABEL)
                        ]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format={SystemConstants.DATE_FORMAT}
                            disabledDate={(current) => {
                                let customDate = dayjs().add(-30, 'day')
                                return current && current < customDate
                            }}
                            onChange={handleStartDateChange}
                            disabled={props.initialValues?.id ? true : false}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.PAY_DATE.LABEL}
                        name={"paymentDay"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.PAY_DATE.LABEL)
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.PAY_DATE.PLACEHOLDER}
                            onChange={handlePaymentDayChange}
                            readOnly={!isMonthlyContract}
                            disabled={!isMonthlyContract}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.LABEL}
                        name={"nextServiceDebit"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.LABEL)
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.NEXT_SERVICE_DEBIT_DATE.PLACEHOLDER}
                            readOnly
                            disabled={!isMonthlyContract}
                        />
                    </Form.Item>
                </Col6>
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
